// ═══════════════════════════════════════════════════════════════════════════
// RUN 23: Section Spacing (Heading Paragraph Spacing)
// ═══════════════════════════════════════════════════════════════════════════
import { safeAlert, reportResult, paraDetail, RunDetail } from '../runs/common';
import { getConfig, SectionSpacingConfig } from '../taskpane/configService';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface SpacingConfig {
  targetSpaceBefore: number;
  targetSpaceAfter: number;
  headingStyleRe: RegExp;
  paraBlockRe: RegExp;
  spacingTagRe: RegExp;
  beforeTwips: string;
  afterTwips: string;
  tocStylePrefixes: string;
  tocStyleExact: string;
  bookmarkPrefixes: string;
  bookmarkRegex: string;
}

interface SpacingCheckResult {
  isBeforeAuto: boolean;
  isAfterAuto: boolean;
  paraOoxml: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper: Build config object
// ─────────────────────────────────────────────────────────────────────────────

function buildSpacingConfig(c: SectionSpacingConfig): SpacingConfig {
  return {
    targetSpaceBefore: c.target_space_before,
    targetSpaceAfter:  c.target_space_after,
    headingStyleRe:    new RegExp(c.heading_style_pattern, "i"),
    paraBlockRe:       new RegExp(c.ooxml.paragraph_block_regex),
    spacingTagRe:      new RegExp(c.ooxml.spacing_tag_regex),
    beforeTwips:       String(Math.round(c.target_space_before * 20)),
    afterTwips:        String(Math.round(c.target_space_after  * 20)),
    tocStylePrefixes:  c.toc_style_prefixes,
    tocStyleExact:     c.toc_style_exact,
    bookmarkPrefixes:  c.bookmark_prefixes,
    bookmarkRegex:     c.bookmark_regex,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper: Collect TOC bookmark names from body paragraphs
// ─────────────────────────────────────────────────────────────────────────────

async function collectTocBookmarks(
  context: Word.RequestContext,
  cfg: SpacingConfig,
): Promise<Set<string>> {
  const tocBookmarks = new Set<string>();
  const bodyParas = context.document.body.paragraphs;
  bodyParas.load("items");
  await context.sync();

  for (const para of bodyParas.items) {
    para.load("style");
  }
  await context.sync();

  for (const para of bodyParas.items) {
    const style = (para.style || "").toLowerCase().replace(/\s/g, "");
    const isToc = style.startsWith(cfg.tocStylePrefixes) || style === cfg.tocStyleExact;
    if (!isToc) continue;

    try {
      const ooxmlResult = para.getOoxml();
      await context.sync();
      extractAnchors(ooxmlResult.value, tocBookmarks);
    } catch (err) {
      console.warn("  Could not read OOXML for TOC paragraph:", (err as Error).message);
    }
  }

  return tocBookmarks;
}

function extractAnchors(ooxml: string, target: Set<string>): void {
  const anchorRe = /w:anchor="([^"]+)"/g;
  let m: RegExpExecArray | null;
  while ((m = anchorRe.exec(ooxml)) !== null) {
    target.add(m[1]);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper: Check if a Heading 1 paragraph has a TOC bookmark
// ─────────────────────────────────────────────────────────────────────────────

async function heading1HasTocBookmark(
  para: Word.Paragraph,
  context: Word.RequestContext,
  tocBookmarks: Set<string>,
  cfg: SpacingConfig,
): Promise<boolean> {
  try {
    const ooxmlResult = para.getOoxml();
    await context.sync();
    return ooxmlContainsTocBookmark(ooxmlResult.value, tocBookmarks, cfg);
  } catch (err) {
    console.warn("  Could not read OOXML for Heading 1:", (err as Error).message);
    return false;
  }
}

function ooxmlContainsTocBookmark(
  ooxml: string,
  tocBookmarks: Set<string>,
  cfg: SpacingConfig,
): boolean {
  const nameRe = /w:name="([^"]+)"/g;
  const bookmarkRegExp = new RegExp(cfg.bookmarkRegex);
  let m: RegExpExecArray | null;
  while ((m = nameRe.exec(ooxml)) !== null) {
    const bkName = m[1];
    const isKnownPrefix = bkName.startsWith(cfg.bookmarkPrefixes);
    if ((isKnownPrefix || bookmarkRegExp.test(bkName)) && tocBookmarks.has(bkName)) {
      return true;
    }
  }
  return false;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper: Detect auto-spacing flags via OOXML
// ─────────────────────────────────────────────────────────────────────────────

async function detectSpacingFlags(
  para: Word.Paragraph,
  context: Word.RequestContext,
  cfg: SpacingConfig,
): Promise<SpacingCheckResult> {
  try {
    const ooxmlResult = para.getOoxml();
    await context.sync();
    const paraOoxml = ooxmlResult.value;
    const paraBlockMatch = cfg.paraBlockRe.exec(paraOoxml);
    if (paraBlockMatch) {
      const paraBlock = paraBlockMatch[0];
      return {
        isBeforeAuto: /w:beforeAutospacing="1"/.test(paraBlock),
        isAfterAuto:  /w:afterAutospacing="1"/.test(paraBlock),
        paraOoxml,
      };
    }
    return { isBeforeAuto: false, isAfterAuto: false, paraOoxml };
  } catch (err) {
    console.warn("  Could not read spacing OOXML:", (err as Error).message);
    return { isBeforeAuto: false, isAfterAuto: false, paraOoxml: "" };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper: Patch OOXML to remove auto-spacing flags and set explicit values
// ─────────────────────────────────────────────────────────────────────────────

function buildPatchedOoxml(
  paraOoxml: string,
  isBeforeAuto: boolean,
  isAfterAuto: boolean,
  cfg: SpacingConfig,
): string | null {
  const paraBlockMatch = cfg.paraBlockRe.exec(paraOoxml);
  if (!paraBlockMatch) return null;

  const paraBlock      = paraBlockMatch[0];
  const paraBlockStart = paraOoxml.indexOf(paraBlock);
  let patchedBlock     = paraBlock;

  if (isBeforeAuto) patchedBlock = patchedBlock.replace(/\s*w:beforeAutospacing="1"/g, "");
  if (isAfterAuto)  patchedBlock = patchedBlock.replace(/\s*w:afterAutospacing="1"/g, "");

  const spacingMatch = cfg.spacingTagRe.exec(patchedBlock);
  if (spacingMatch) {
    patchedBlock = patchedBlock.replace(spacingMatch[0], patchSpacingTag(spacingMatch[0], cfg));
  }

  return (
    paraOoxml.substring(0, paraBlockStart) +
    patchedBlock +
    paraOoxml.substring(paraBlockStart + paraBlock.length)
  );
}

function patchSpacingTag(spacingTag: string, cfg: SpacingConfig): string {
  let tag = spacingTag;
  tag = /w:before="[^"]*"/.test(tag)
    ? tag.replace(/w:before="[^"]*"/, `w:before="${cfg.beforeTwips}"`)
    : tag.replace("<w:spacing", `<w:spacing w:before="${cfg.beforeTwips}"`);

  tag = /w:after="[^"]*"/.test(tag)
    ? tag.replace(/w:after="[^"]*"/, `w:after="${cfg.afterTwips}"`)
    : tag.replace("<w:spacing", `<w:spacing w:after="${cfg.afterTwips}"`);

  return tag;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper: Apply OOXML fix (auto spacing case)
// ─────────────────────────────────────────────────────────────────────────────

async function applyOoxmlSpacingFix(
  para: Word.Paragraph,
  context: Word.RequestContext,
  paraOoxml: string,
  isBeforeAuto: boolean,
  isAfterAuto: boolean,
  cfg: SpacingConfig,
): Promise<boolean> {
  const patchedOoxml = buildPatchedOoxml(paraOoxml, isBeforeAuto, isAfterAuto, cfg);
  if (!patchedOoxml) {
    console.log("    ⚠ Could not find <w:p> block — skipped");
    return false;
  }

  context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
  await context.sync();

  para.insertOoxml(patchedOoxml, "Replace");
  await context.sync();

  console.log("    Auto spacing removed + spacing set via OOXML ✓");
  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper: Apply API fix (normal spacing case)
// ─────────────────────────────────────────────────────────────────────────────

async function applyApiSpacingFix(
  para: Word.Paragraph,
  context: Word.RequestContext,
  currentBefore: number,
  currentAfter: number,
  cfg: SpacingConfig,
): Promise<void> {
  context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

  if (currentBefore !== cfg.targetSpaceBefore) para.spaceBefore = cfg.targetSpaceBefore;
  if (currentAfter  !== cfg.targetSpaceAfter)  para.spaceAfter  = cfg.targetSpaceAfter;
  await context.sync();

  console.log("    Spacing fixed via API with track changes ✓");
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper: Process a single heading paragraph
// Returns true if a fix was applied, false if skipped or already correct.
// ─────────────────────────────────────────────────────────────────────────────

async function processHeadingPara(
  para: Word.Paragraph,
  index: number,
  context: Word.RequestContext,
  tocBookmarks: Set<string>,
  cfg: SpacingConfig,
  counters: { alreadyCorrect: number; heading1Skipped: number },
): Promise<RunDetail | null> {
  const style    = para.style || "";
  const paraText = (para.text || "").trim();
  const level    = Number.parseInt(style.replace(/\D/g, ""), 10);

  // Heading 1: skip if not linked from TOC
  if (level === 1 && tocBookmarks.size > 0) {
    const linked = await heading1HasTocBookmark(para, context, tocBookmarks, cfg);
    if (!linked) {
      counters.heading1Skipped++;
      console.log(`  [${index}] Heading 1: "${paraText.substring(0, 40)}" — no TOC bookmark, skipped`);
      return null;
    }
  }

  const currentBefore = para.spaceBefore;
  const currentAfter  = para.spaceAfter;

  const { isBeforeAuto, isAfterAuto, paraOoxml } = await detectSpacingFlags(para, context, cfg);

  const needsBeforeFix = isBeforeAuto || currentBefore !== cfg.targetSpaceBefore;
  const needsAfterFix  = isAfterAuto  || currentAfter  !== cfg.targetSpaceAfter;

  if (!needsBeforeFix && !needsAfterFix) {
    counters.alreadyCorrect++;
    return null;
  }

  console.log(
    `  [${index}] ${style}: "${paraText.substring(0, 40)}" | ` +
    `Before: ${isBeforeAuto ? "Auto" : currentBefore}→${cfg.targetSpaceBefore}, ` +
    `After: ${isAfterAuto ? "Auto" : currentAfter}→${cfg.targetSpaceAfter}`,
  );

  let fixed = false;

  if ((isBeforeAuto || isAfterAuto) && paraOoxml) {
    console.log("    Auto spacing detected — using OOXML to remove auto + set spacing");
    fixed = await applyOoxmlSpacingFix(para, context, paraOoxml, isBeforeAuto, isAfterAuto, cfg);
  } else {
    await applyApiSpacingFix(para, context, currentBefore, currentAfter, cfg);
    fixed = true;
  }

  return fixed ? paraDetail(para.uniqueLocalId, paraText, style) : null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper: Report final results
// ─────────────────────────────────────────────────────────────────────────────

function reportSpacingResult(
  spacingFixed: number,
  heading1Skipped: number,
  changedParas: RunDetail[],
): void {
  if (spacingFixed > 0) {
    reportResult('Section Spacing Complete', [
      { label: 'Heading spacing fixed', count: spacingFixed },
      { label: 'Standard spacing applied', note: 'spaceBefore 12pt, spaceAfter 6pt' },
      ...changedParas,
    ]);
  } else {
    reportResult('Section Spacing Complete', [
      { label: 'No changes needed', note: 'Correct spacings found in the doc' },
      ...(heading1Skipped > 0
        ? [{ label: 'Heading 1 skipped (no TOC link)', count: heading1Skipped }]
        : []),
    ]);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Main entry point
// ─────────────────────────────────────────────────────────────────────────────

export async function run23() {

  const cfg = buildSpacingConfig(getConfig().run23_sectionSpacing);


  await Word.run(async (context) => {
    try {
      console.log("═".repeat(70));
      console.log("RUN 23: Section Spacing (Heading Paragraph Spacing)");
      console.log("═".repeat(70));

      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

      // ── Step 1: Collect TOC bookmark references ──
      console.log("\n── Step 1: Collecting TOC bookmark references ──");
      const tocBookmarks = await collectTocBookmarks(context, cfg);
      console.log(`  TOC bookmark references found: ${tocBookmarks.size}`);

      // ── Step 2: Fix spacing on heading paragraphs ──
      console.log("\n── Step 2: Fixing heading paragraph spacing ──");

      const allParas = context.document.body.paragraphs;
      allParas.load("items");
      await context.sync();

      for (const para of allParas.items) {
        para.load("style, text, spaceBefore, spaceAfter, uniqueLocalId");
      }
      await context.sync();

      const totalParas = allParas.items.length;
      console.log(`  Total paragraphs: ${totalParas}`);

      let spacingFixed = 0;
      const changedParas: RunDetail[] = [];
      const counters = { alreadyCorrect: 0, heading1Skipped: 0 };

      for (let i = 0; i < totalParas; i++) {
        const para = allParas.items[i];
        if (!(para.style || "").match(cfg.headingStyleRe)) continue;

        const detail = await processHeadingPara(para, i, context, tocBookmarks, cfg, counters);
        if (detail) {
          spacingFixed++;
          changedParas.push(detail);
        }
      }

      // ── Summary ──
      context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
      await context.sync();

      console.log("\n" + "═".repeat(70));
      console.log(`Spacing fixed: ${spacingFixed}`);
      console.log(`Already correct: ${counters.alreadyCorrect}`);
      console.log(`Heading 1 skipped (no TOC link): ${counters.heading1Skipped}`);
      console.log("✓ Section Spacing check complete!");
      console.log("═".repeat(70));

      reportSpacingResult(spacingFixed, counters.heading1Skipped, changedParas);

    } catch (error) {
      console.error("Error in run23:", error);
      console.error("Message:", (error as Error).message);
      safeAlert("An error occurred during Section Spacing check.\nCheck console (F12) for details.");
    }
  });
}